import { useMemo, useState } from 'react';
import { ColumnType, SortConfig } from '../components/datatable/utils/type';

export function useSorting<T>(
  rows: T[],
  columns: ColumnType<T>[],
  enabled: boolean,
  initial?: SortConfig
) {
  const [sortConfig, setSort] = useState<SortConfig>(
    initial ?? { columnIndex: -1, direction: 'asc' }
  );

  const normalize = (value: any): string | number => {
    if (value == null) return '';

    if (typeof value === 'string' || typeof value === 'number') {
      return value;
    }

    if (Array.isArray(value)) {
      return value.map(normalize).join(' ');
    }

    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return '';
      }
    }

    return String(value);
  };

  const sortedRows = useMemo(() => {
    if (!enabled || sortConfig.columnIndex === -1) return rows;

    const column = columns[sortConfig.columnIndex];
    if (!column?.sortable) return rows;

    const getValue = (row: T) => {
      if (column.selector) return normalize(column.selector(row));
      return '';
    };

    return [...rows].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);

      if (typeof av === 'number' && typeof bv === 'number') {
        return sortConfig.direction === 'asc' ? av - bv : bv - av;
      }

      return (
        String(av).localeCompare(String(bv), 'es', { sensitivity: 'base' }) *
        (sortConfig.direction === 'asc' ? 1 : -1)
      );
    });
  }, [rows, columns, enabled, sortConfig]);

  return { sortedRows, sortConfig, setSort };
}
